'use client';

import typography from './HomepageTypography.module.css';

import { useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { cn } from '@/lib/cn';
import styles from './HomepageStyleStory.module.css';

const STYLE_STEPS = ['reference', 'save', 'create', 'motion'] as const;

export function HomepageStyleFilm({ className }: { className?: string }) {
  const t = useTranslations('auth.cinematic.styleStory');
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    let visible = false;
    const connection = (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
    const lightweight = window.matchMedia('(max-width: 767px)').matches
      || connection?.saveData
      || /(^|-)2g$/.test(connection?.effectiveType ?? '');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    const prepare = () => {
      if (video.hasAttribute('src')) return;
      video.src = `/login-gallery/style-film/style-story-v25-${lightweight ? 'mobile' : 'desktop'}.mp4`;
      video.preload = 'auto';
      video.load();
    };
    const syncPlayback = () => {
      if (!visible || document.hidden || reducedMotion.matches) {
        video.pause();
        return;
      }
      prepare();
      // Autoplay can be denied by the browser; the poster remains the static presentation.
      void video.play().catch(() => undefined);
    };
    const warmup = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        if (!reducedMotion.matches) prepare();
        warmup.disconnect();
      }
    }, { rootMargin: '800px 0px' });
    const playback = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting && entry.intersectionRatio >= 0.15;
      syncPlayback();
    }, { threshold: [0, 0.15] });

    warmup.observe(video);
    playback.observe(video);
    document.addEventListener('visibilitychange', syncPlayback);
    reducedMotion.addEventListener('change', syncPlayback);
    return () => {
      warmup.disconnect();
      playback.disconnect();
      document.removeEventListener('visibilitychange', syncPlayback);
      reducedMotion.removeEventListener('change', syncPlayback);
      video.pause();
    };
  }, []);

  return (
    <section id="style-film" className={cn(styles.filmChapter, 'scroll-mt-24', className)}>
      <div className={styles.filmInner}>
        <div className={styles.filmHeader} data-homepage-reveal="0">
          <h2 className={styles.filmTitle}>{t('title')}</h2>
          <p className={styles.filmIntro}>{t('intro')}</p>
        </div>

        <div className={styles.filmLayout} data-homepage-reveal="1">
          <ol className={styles.filmSteps}>
            {STYLE_STEPS.map((step, index) => (
              <li key={step} className={cn(styles.filmStep, styles[`filmStep${index + 1}`])}>
                <span className={styles.filmStepNumber} aria-hidden="true">{index + 1}</span>
                <div className={styles.filmStepCopy}>
                  <h3 className={typography.title}>{t(`steps.${step}.title`)}</h3>
                  <p>{t(`steps.${step}.body`)}</p>
                </div>
              </li>
            ))}
          </ol>

          <figure className={styles.filmVisual} aria-label={t('showcaseLabel')}>
            <video
              ref={videoRef}
              className={styles.filmVideo}
              muted
              playsInline
              loop
              preload="none"
              poster="/login-gallery/style-film/poster-v25.jpg"
              disablePictureInPicture
              tabIndex={-1}
              aria-hidden="true"
            />
          </figure>
        </div>
      </div>
    </section>
  );
}
